import type { HttpClient } from '@/lib/http/client.js'
import type {
  PromotionDetail,
  PromotionDiagnostics,
  PromotionPage,
  PromotionSearch,
  RepublishResult,
  StatusChangeResult,
} from '@/types/promotion/promotion.type.js'

/**
 * `admin/promotions`, verified against `promotion-admin.controller.ts` and
 * `promotion-write-admin.controller.ts`.
 *
 * ## Three writes, and deliberately not the fourth
 *
 * `PATCH /admin/promotions/:id` replaces a promotion's configuration **wholesale** — tiers,
 * products, placement, visibility, widgets — from a body the merchant editor assembles. The
 * detail view this panel reads is a different shape (`sections`, `discount_tiers`,
 * `trigger_product`… have no DTO counterpart), so an edit assembled here would silently
 * drop configuration on a live promotion. That write is therefore not exposed until the
 * editor's write mappers are ported. Republish, status and delete are well-defined and are.
 */
export interface PromotionService {
  search: (query: PromotionSearch) => Promise<PromotionPage>
  /** Loads by id alone — reaches templates and soft-deleted rows the merchant route cannot. */
  get: (id: string) => Promise<PromotionDetail>
  /** Reads Shopify live. Called on demand, never on load. */
  diagnostics: (id: string) => Promise<PromotionDiagnostics>
  /** Sends the promotion AS STORED to Shopify again. Changes nothing; `metafieldsSet` is an upsert. */
  republish: (id: string) => Promise<RepublishResult>
  /** A state, not a flip. Shopify rewrites the discount's schedule on a status change. */
  setActive: (id: string, active: boolean) => Promise<StatusChangeResult>
}

export function createPromotionService(http: HttpClient): PromotionService {
  return {
    search: (query) => http.get<PromotionPage>('admin/promotions', { query: { ...query } }),
    get: (id) => http.get<PromotionDetail>(`admin/promotions/${encodeURIComponent(id)}`),
    diagnostics: (id) =>
      http.get<PromotionDiagnostics>(`admin/promotions/${encodeURIComponent(id)}/diagnostics`),
    republish: (id) => http.post<RepublishResult>(`admin/promotions/${encodeURIComponent(id)}/republish`),
    setActive: (id, active) =>
      http.patch<StatusChangeResult>(`admin/promotions/${encodeURIComponent(id)}/status`, { is_active: active }),
  }
}
